import { CommonModule, NgComponentOutlet } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TableComponent, ColumnTemplateDirective } from '@table';
import { PaginationComponent } from '@pagination';
import { SelectComponent, SelectItem } from '@select';
import { injectDispatch } from '@ngrx/signals/events';
import { messagingPageEvents } from '@messaging/events';
import { MessagingStore } from '../../../../../data-access/src/index';
import { Message, MessageCategory } from '../../../../../util/src/index';
import { MessageContentPipe } from '../../../../../components/pipes/src/index';
import { MessagingDialogService } from '@messaging/components/services';
import { ServiceUpgradeInjectorService } from '@shims';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'messaging-messages',
  standalone: true,
  imports: [
    CommonModule,
    NgComponentOutlet,
    ReactiveFormsModule,
    TableComponent,
    ColumnTemplateDirective,
    SelectComponent,
    PaginationComponent,
    MessageContentPipe,
  ],
  providers: [],
  templateUrl: './messages.component.html',
  styleUrls: [
    '../../../../../../ui-components/src/lib/_styles/abstracts/_colors.scss',
    '../../../../../../ui-components/src/lib/_styles/abstracts/_typography.scss',
    '../../../../../../ui-components/src/lib/_styles/abstracts/_spacing.scss',
    './messages.component.scss',
  ],
})
export class MessagesComponent implements OnInit, OnDestroy {
  protected readonly store = inject(MessagingStore);
  private readonly dispatch = injectDispatch(messagingPageEvents);
  private readonly dialogService = inject(MessagingDialogService);
  protected readonly messages = computed(() => this.store.messages());
  protected readonly isLoading = computed(() => this.store.isLoading());
  protected readonly metadata = computed(() => this.store.metadata());
  protected readonly pagination = computed(() => this.store.pagination());
  protected readonly filter = computed(() => this.store.filter());
  private readonly upgradeService = inject(ServiceUpgradeInjectorService);
  private readonly $rootScope = this.upgradeService.getAngularJsRootScope();
  private readonly $state = this.upgradeService.getAngularJsUiState();
  readonly isInitialLoad = computed(
    () => this.store.messagesResource.isLoading() && !this.store.messagesResource.hasValue(),
  );
  private deregisterNavigate?: () => void;

  protected readonly categoryOptions = computed<SelectItem<MessageCategory | null>[]>(() => [
    { id: 0, label: 'All Categories', value: null },
    ...this.store.categories().map(c => ({ id: c.id, label: c.name, value: c })),
  ]);

  protected readonly columns = [
    { key: 'name', label: 'Name' },
    { key: 'content', label: 'Last Message' },
    { key: 'sent', label: 'Date' },
    { key: 'status', label: 'Status' },
  ];

  protected readonly readControl = new FormControl<SelectItem<string> | null>({
    id: 0,
    label: 'Unread',
    value: 'false',
  });
  protected readonly orderControl = new FormControl<SelectItem<string> | null>({
    id: 0,
    label: 'Sort new to old',
    value: 'desc',
  });
  protected readonly categoryControl = new FormControl<SelectItem<MessageCategory | null> | null>(null);

  protected readonly readOptions: SelectItem<string>[] = [
    { id: 0, label: 'Unread', value: 'false' },
    { id: 1, label: 'Read', value: 'true' },
  ];
  protected readonly orderOptions: SelectItem<string>[] = [
    { id: 0, label: 'Sort new to old', value: 'desc' },
    { id: 1, label: 'Sort old to new', value: 'asc' },
  ];

  protected readonly currentPage = computed(() => {
    const p = this.pagination();
    return Math.floor(p.offset / p.limit) + 1;
  });

  protected readonly totalPages = computed(() => {
    const p = this.pagination();
    return Math.ceil(p.total / p.limit) || 1;
  });

  ngOnInit() {
    this.store.loadMessages();

    this.readControl.valueChanges.subscribe(opt => {
      if (opt) this.store.applyFilter({ read: opt.value as 'true' | 'false' });
    });

    this.orderControl.valueChanges.subscribe(opt => {
      if (opt) this.store.applyFilter({ order: opt.value as 'asc' | 'desc' });
    });

    this.categoryControl.valueChanges.subscribe(opt => {
      this.store.applyFilter({ categoryId: opt?.value?.id ?? 0 });
    });
    this.deregisterNavigate = this.$rootScope.$on(
      'dialog:navigate',
      (_event: unknown, navData: { state: string; params: Record<string, unknown> }) => {
        this.$state.go(navData.state, navData.params);
      },
    );
  }

  protected onPageChange(page: number) {
    this.store.goToPage(page);
  }

  protected openPatientMessages(message: Message) {
    this.dispatch.patientIdSelected({ patientId: message.from.id });
    this.dialogService.openChat();
  }

  protected emptyStateMessage(): string {
    return this.filter().read === 'false' ? 'No Unread Messages' : 'No Messages';
  }

  ngOnDestroy() {
    this.deregisterNavigate?.();
  }
}
